'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/app/[lang]/(main)/components/shared/PrivateLink/ServerLink';
import { useGeoCategoryHref } from '@/app/[lang]/(main)/components/main-page/CategoriesShortcuts/GeoAwareCategoryLink.client';
import { useGetCategoryIdDict } from '@/hooks/useGetCategoryIdDict';

const pillClassName =
  'inline-flex max-w-full items-center rounded-[16px] bg-bg2-day px-[12px] py-[5px] text-[14px] leading-[18px] text-text-primary break-words';

type SingleValueProps = {
  categoryId: string;
  parameterId: string;
  value: string;
  hint: string;
};

const SingleValue: React.FC<SingleValueProps> = ({ categoryId, parameterId, value, hint }) => {
  const parameters = useMemo(() => [{ id: parameterId, value }], [parameterId, value]);
  const { data: categoryIdDict } = useGetCategoryIdDict();
  const href = useGeoCategoryHref({ categoryId, parameters });

  if (!categoryId || !categoryIdDict[categoryId] || !href) {
    return <span className={pillClassName}>{value}</span>;
  }

  return (
    <Link
      href={href}
      title={hint}
      className={`${pillClassName} transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-[#ECEEF4] hover:shadow-[0_1px_6px_rgba(24,24,24,0.09)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/25 active:scale-[0.97] active:bg-[#E4E6ED] active:shadow-none`}
    >
      {value}
    </Link>
  );
};

type Props = {
  categoryId: string;
  parameterId: string;
  values: string[];
};

export const ParameterValueLinks: React.FC<Props> = ({ categoryId, parameterId, values }) => {
  const t = useTranslations('product');
  const hint = t('parameter_filter_hint');

  return (
    <>
      {values.map((value, index) => (
        <SingleValue
          key={`${value}-${index}`}
          categoryId={categoryId}
          parameterId={parameterId}
          value={value}
          hint={hint}
        />
      ))}
    </>
  );
};
